/**
 * StatusFeedback - Displays success and error messages
 *
 * Shows toast-style notifications for operations results
 * Requirement: ES-REQ-038, ES-REQ-039
 */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useLabeling } from '@/context/labelingContext';

const Container = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  max-width: 400px;
`;

const Message = styled.div<{ $type: 'success' | 'error' }>`
  padding: 16px 20px;
  border-radius: 8px;
  background-color: ${props => props.$type === 'success' ? 'var(--color-success-500)' : 'var(--color-error-300)'};
  color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0 0 0 12px;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`;

export const StatusFeedback: React.FC = () => {
  const { state, clearError, clearSuccess } = useLabeling();

  // Auto-clear success message after 3 seconds
  useEffect(() => {
    if (state.successMessage) {
      const timer = setTimeout(() => {
        clearSuccess();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [state.successMessage, clearSuccess]);

  return (
    <Container>
      {state.error && (
        <Message $type="error">
          <span>{state.error}</span>
          <CloseButton onClick={clearError} aria-label="Close error message">
            ×
          </CloseButton>
        </Message>
      )}
      {state.successMessage && (
        <Message $type="success">
          <span>{state.successMessage}</span>
          <CloseButton onClick={clearSuccess} aria-label="Close success message">
            ×
          </CloseButton>
        </Message>
      )}
    </Container>
  );
};

export default StatusFeedback;
